/**
 * HazardNotificationContext - Real-time hazard notifications
 * 
 * Polls for new hazards and displays snackbar alerts to all users.
 * Shows hazard type, location, and allows dismissal.
 * 
 * HAZARD CONFIRMATION: When users pass ANY reported hazard, shows
 * human-friendly prompt asking if it's still there. Community validation!
 * 
 * "Someone out here has my back, even if I've never met them."
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Snackbar, Alert, Box, Typography, IconButton, Slide, Dialog, Button, Chip, Collapse } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Close as CloseIcon,
  NavigateNext as NavigateIcon,
  ThumbUp as YesIcon,
  ThumbDown as NoIcon,
  CheckCircle as ConfirmIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as TimeIcon,
  NearMe as DistanceIcon,
} from '@mui/icons-material';
import { feedbackService } from '../services/feedback';
import { telegram } from '../services/telegram';
import { storeExternalFix } from '../services/highPrecisionLocation';

// Material UI style hazard icons with dark backgrounds
const hazardIcons = {
  crash: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23ef4444' stroke-width='1.5'/><path d='M28 18L22 32h5l-2 8 10-16h-5l3-6z' fill='%23ef4444'/></svg>`,
  slowdown: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23f59e0b' stroke-width='1.5'/><circle cx='28' cy='28' r='12' fill='none' stroke='%23f59e0b' stroke-width='3'/><path d='M28 16v24M40 28H16' stroke='%23f59e0b' stroke-width='2' stroke-linecap='round'/></svg>`,
  construction: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23f97316' stroke-width='1.5'/><path d='M16 36h24M18 32l2-12 4 12 4-12 4 12 4-12 2 12M16 38h24v2H16z' fill='%23f97316'/></svg>`,
  lane_closure: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%238b5cf6' stroke-width='1.5'/><path d='M28 16v24M20 24h16M20 32h16' stroke='%238b5cf6' stroke-width='3' stroke-linecap='round'/><circle cx='28' cy='28' r='14' fill='none' stroke='%238b5cf6' stroke-width='2'/><path d='M20 20l16 16M36 20l-16 16' stroke='%238b5cf6' stroke-width='2'/></svg>`,
  object_on_road: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23eab308' stroke-width='1.5'/><path d='M28 16l-8 24h16l-8-24z' fill='none' stroke='%23eab308' stroke-width='2'/><circle cx='28' cy='24' r='2' fill='%23eab308'/><path d='M28 28v6' stroke='%23eab308' stroke-width='2' stroke-linecap='round'/></svg>`,
  flooded_road: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%230ea5e9' stroke-width='1.5'/><path d='M16 28q3-2 6 0t6 0 6 0 6 0M16 34q3-2 6 0t6 0 6 0 6 0' stroke='%230ea5e9' stroke-width='2' fill='none'/><path d='M22 22q2-3 4 0t4 0M26 18q1-2 2 0t2 0' stroke='%230ea5e9' stroke-width='1.5' fill='none'/></svg>`,
  low_visibility: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%2364748b' stroke-width='1.5'/><path d='M20 28q2-4 8-4t8 4M24 32q1-2 4-2t4 2' stroke='%2364748b' stroke-width='2' fill='none'/><circle cx='28' cy='28' r='3' fill='%2364748b'/><path d='M18 22q3-2 10-2t10 2' stroke='%2364748b' stroke-width='1.5' fill='none' opacity='0.5'/></svg>`,
  cop: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%233b82f6' stroke-width='1.5'/><path d='M28 18l-2 4-4 1 3 3-1 4 4-2 4 2-1-4 3-3-4-1z' fill='%233b82f6'/><rect x='22' y='26' width='12' height='10' rx='1' fill='none' stroke='%233b82f6' stroke-width='2'/></svg>`,
};

// Haversine distance calculation (meters)
function getDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// PROXIMITY NOTIFICATION THRESHOLDS
// These determine when users are notified about nearby hazards
// Different thresholds for notification vs confirmation

// Initial notification radius - warn users when approaching (larger radius)
const NOTIFICATION_RADIUS: Record<string, number> = {
  cop: 2000,              // 2km ahead - give drivers time to slow down
  crash: 1500,            // 1.5km - major obstruction, early warning
  slowdown: 1200,         // 1.2km - traffic backup ahead
  construction: 1000,     // 1km - planned work zone
  lane_closure: 1500,     // 1.5km - road closure, need alternate route
  object_on_road: 800,    // 800m - debris/obstacle
  flooded_road: 1500,     // 1.5km - road impassable, early warning critical
  low_visibility: 1000,   // 1km - weather conditions ahead
};

// Confirmation radius - ask if still there when passing by (smaller radius)
const CONFIRM_RADIUS: Record<string, number> = {
  cop: 500,               // 500m - close enough to confirm
  crash: 750,             // 750m - can see if still blocking
  slowdown: 600,          // 600m - in the traffic now
  construction: 500,      // 500m - can see work zone
  lane_closure: 750,      // 750m - at the closure point
  object_on_road: 400,    // 400m - can see the debris
  flooded_road: 800,      // 800m - can see water on road
  low_visibility: 500,    // 500m - experiencing conditions
};

// Default fallback values
const DEFAULT_NOTIFICATION_RADIUS = 1000; // 1km
const DEFAULT_CONFIRM_RADIUS = 500;       // 500m

interface HazardReport {
  id: string;
  type: string;
  latitude: number;
  longitude: number;
  note: string | null;
  context_note?: string | null;
  is_ghost: boolean;
  confirmations?: number;
  still_there_votes?: number;
  gone_votes?: number;
  extension_applied?: boolean;
  created_at: string;
  expires_at: string;
}

// Human-friendly questions for each hazard type
const HAZARD_QUESTIONS: Record<string, { question: string; yesLabel: string; noLabel: string; emoji: string }> = {
  cop: { question: 'Bear still there?', yesLabel: 'YES - Still there', noLabel: 'NO - Gone', emoji: '🐻' },
  crash: { question: 'Crash still blocking?', yesLabel: 'Still there', noLabel: 'Cleared', emoji: '💥' },
  slowdown: { question: 'Traffic still slow?', yesLabel: 'Still slow', noLabel: 'Moving now', emoji: '🐢' },
  construction: { question: 'Construction active?', yesLabel: 'Yes, active', noLabel: 'No activity', emoji: '🚧' },
  lane_closure: { question: 'Lane still closed?', yesLabel: 'Still closed', noLabel: 'Open now', emoji: '⛔' },
  object_on_road: { question: 'Object still there?', yesLabel: 'Still there', noLabel: 'Cleared', emoji: '⚠️' },
  flooded_road: { question: 'Road still flooded?', yesLabel: 'Still flooded', noLabel: 'Passable', emoji: '🌊' },
  low_visibility: { question: 'Visibility still low?', yesLabel: 'Still bad', noLabel: 'Better now', emoji: '🌫️' },
  safety_inspector: { question: 'Inspector still on site?', yesLabel: 'YES - Still there', noLabel: 'NO - Gone', emoji: '👷' },
};

interface HazardNotification {
  hazard: HazardReport;
  timestamp: number;
}

interface HazardNotificationContextType {
  hazards: HazardReport[];
  showHazardNotification: (hazard: HazardReport) => void;
  dismissNotification: () => void;
  navigateToHazard: (hazard: HazardReport) => void;
  voteOnHazard: (hazardId: string, vote: 'yes' | 'no') => Promise<void>;
  zoomToHazard: (hazard: HazardReport) => void;
  setZoomCallback: (callback: ((lat: number, lng: number, zoom?: number) => void) | null) => void;
}

const HazardNotificationContext = createContext<HazardNotificationContextType | null>(null);

// Hazard type metadata
const HAZARD_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  crash: { icon: <Box component="img" src={hazardIcons.crash} sx={{ width: 32, height: 32 }} />, color: '#ef4444', label: 'Crash' },
  slowdown: { icon: <Box component="img" src={hazardIcons.slowdown} sx={{ width: 32, height: 32 }} />, color: '#f59e0b', label: 'Slowdown' },
  construction: { icon: <Box component="img" src={hazardIcons.construction} sx={{ width: 32, height: 32 }} />, color: '#f97316', label: 'Construction' },
  lane_closure: { icon: <Box component="img" src={hazardIcons.lane_closure} sx={{ width: 32, height: 32 }} />, color: '#8b5cf6', label: 'Lane Closure' },
  object_on_road: { icon: <Box component="img" src={hazardIcons.object_on_road} sx={{ width: 32, height: 32 }} />, color: '#eab308', label: 'Object on Road' },
  flooded_road: { icon: <Box component="img" src={hazardIcons.flooded_road} sx={{ width: 32, height: 32 }} />, color: '#0ea5e9', label: 'Flooded Road' },
  low_visibility: { icon: <Box component="img" src={hazardIcons.low_visibility} sx={{ width: 32, height: 32 }} />, color: '#64748b', label: 'Low Visibility' },
  cop: { icon: <Box component="img" src={hazardIcons.cop} sx={{ width: 32, height: 32 }} />, color: '#3b82f6', label: 'Cop Spotted' },
};

// Slide transition for snackbar - slides down from top
function SlideTransition(props: TransitionProps & { children: React.ReactElement }) {
  return <Slide {...props} direction="down" />;
}

interface HazardNotificationProviderProps {
  children: ReactNode;
  pollInterval?: number; // milliseconds
  enabled?: boolean;
}

export const HazardNotificationProvider: React.FC<HazardNotificationProviderProps> = ({
  children,
  pollInterval = 15000, // Poll every 15 seconds for faster hazard propagation
  enabled = true,
}) => {
  const [hazards, setHazards] = useState<HazardReport[]>([]);
  const [notification, setNotification] = useState<HazardNotification | null>(null);
  const [notificationExpanded, setNotificationExpanded] = useState(false);
  const [seenHazardIds, setSeenHazardIds] = useState<Set<string>>(() => {
    // Load seen hazards from localStorage
    const saved = localStorage.getItem('seen_hazard_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // Track hazards user has voted on (persistent in localStorage)
  const [votedHazardIds, setVotedHazardIds] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('voted_hazard_ids');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // Callback for zooming the map to a hazard location
  const zoomCallbackRef = useRef<((lat: number, lng: number, zoom?: number) => void) | null>(null);
  
  // Hazard confirmation state (works for ALL hazard types now)
  const [hazardConfirmDialog, setHazardConfirmDialog] = useState<HazardReport | null>(null);
  // Track hazard IDs with timestamps for 30-min cooldown (hazardId -> timestamp)
  const [askedHazardTimes, setAskedHazardTimes] = useState<Map<string, number>>(() => {
    const saved = localStorage.getItem('asked_hazard_times');
    if (saved) {
      try {
        return new Map(JSON.parse(saved));
      } catch {
        return new Map();
      }
    }
    return new Map();
  });
  
  // Track which hazards we've already notified about (hazardId -> timestamp)
  // This prevents re-notifying for the same hazard during a single drive-by
  const [notifiedHazardIds, setNotifiedHazardIds] = useState<Map<string, number>>(new Map());
  
  // Track user's current speed (m/s) for smarter notifications
  const userSpeedRef = useRef<number>(0);
  
  const [votingInProgress, setVotingInProgress] = useState(false);
  
  // Geolocation watch ref
  const watchIdRef = useRef<number | null>(null);
  const userLocationRef = useRef<{ lat: number; lon: number } | null>(null);
  
  // Save asked hazard times to localStorage
  useEffect(() => {
    localStorage.setItem('asked_hazard_times', JSON.stringify(Array.from(askedHazardTimes.entries())));
  }, [askedHazardTimes]);

  // Save voted hazard IDs to localStorage
  useEffect(() => {
    localStorage.setItem('voted_hazard_ids', JSON.stringify(Array.from(votedHazardIds)));
  }, [votedHazardIds]);

  // Fetch hazards from API
  const fetchHazards = useCallback(async () => {
    try {
      const response = await fetch('/api/hazards');
      if (!response.ok) return;
      
      const data = await response.json();
      if (!data.success || !data.hazards) return;
      
      const newHazards: HazardReport[] = data.hazards;
      setHazards(newHazards);
      
      // DON'T show notifications here - let checkHazardProximity handle it
      // Only mark hazards as seen when user is within range
      // This prevents notifications for hazards across the state
    } catch (error) {
      console.error('Error fetching hazards:', error);
    }
  }, []);

  // Save seen hazards to localStorage
  useEffect(() => {
    localStorage.setItem('seen_hazard_ids', JSON.stringify(Array.from(seenHazardIds)));
  }, [seenHazardIds]);
  
  // Two-tier proximity system:
  // 1. NOTIFICATION - Alert user when approaching (larger radius)
  // 2. CONFIRMATION - Ask if still there when passing by (smaller radius)
  const checkHazardProximity = useCallback((position: GeolocationPosition) => {
    const userLat = position.coords.latitude;
    const userLon = position.coords.longitude;
    userLocationRef.current = { lat: userLat, lon: userLon };

    storeExternalFix({
      lat: userLat,
      lng: userLon,
      accuracy:
        typeof position.coords.accuracy === 'number'
          ? Math.max(position.coords.accuracy, 0)
          : null,
      timestamp: position.timestamp,
      source: 'browser-watch',
    });
    
    // Update user speed (m/s)
    if (position.coords.speed !== null && position.coords.speed >= 0) {
      userSpeedRef.current = position.coords.speed;
    }
    
    const now = Date.now();
    const thirtyMinutesMs = 30 * 60 * 1000;
    const fiveMinutesMs = 5 * 60 * 1000; // Notification cooldown
    
    // Sort hazards by distance (closest first)
    const hazardsWithDistance = hazards.map(hazard => ({
      hazard,
      distance: getDistanceMeters(userLat, userLon, hazard.latitude, hazard.longitude)
    })).sort((a, b) => a.distance - b.distance);
    
    for (const { hazard, distance } of hazardsWithDistance) {
      // Skip hazards user has already voted on
      if (votedHazardIds.has(hazard.id)) continue;
      
      // Get radii for this hazard type
      const notifyRadius = NOTIFICATION_RADIUS[hazard.type] || DEFAULT_NOTIFICATION_RADIUS;
      const confirmRadius = CONFIRM_RADIUS[hazard.type] || DEFAULT_CONFIRM_RADIUS;
      
      // TIER 1: INITIAL NOTIFICATION (when approaching)
      // Show notification if within notification radius and haven't notified recently
      if (distance <= notifyRadius) {
        const lastNotified = notifiedHazardIds.get(hazard.id) || 0;
        const shouldNotify = (now - lastNotified) > fiveMinutesMs;
        
        if (shouldNotify && !seenHazardIds.has(hazard.id)) {
          // Show notification with distance/ETA
          showHazardNotification(hazard);
          
          // Mark as notified
          setNotifiedHazardIds(prev => {
            const updated = new Map(prev);
            updated.set(hazard.id, now);
            
            // Clean up old entries (older than 1 hour)
            const oneHourAgo = now - (60 * 60 * 1000);
            for (const [id, time] of updated) {
              if (time < oneHourAgo) updated.delete(id);
            }
            return updated;
          });
          
          // Mark as seen so we don't show it in new hazard notifications
          setSeenHazardIds(prev => {
            const updated = new Set(prev);
            updated.add(hazard.id);
            if (updated.size > 100) {
              const arr = Array.from(updated);
              arr.splice(0, arr.length - 100);
              return new Set(arr);
            }
            return updated;
          });
          
          break; // Only show one notification at a time
        }
      }
      
      // TIER 2: CONFIRMATION DIALOG (when passing by)
      // Ask if hazard is still there when user gets very close
      if (distance <= confirmRadius) {
        if (hazardConfirmDialog) continue; // Don't show multiple dialogs
        
        // Check 30-min cooldown - can ask again after 30 minutes
        const lastAskedTime = askedHazardTimes.get(hazard.id) || 0;
        if (now - lastAskedTime < thirtyMinutesMs) continue;
        
        // User is passing by the hazard! Ask for confirmation
        setHazardConfirmDialog(hazard);
        feedbackService.playSound('notification');
        telegram.hapticFeedback('medium');
        
        // Mark as asked with timestamp for 30-min cooldown
        setAskedHazardTimes(prev => {
          const updated = new Map(prev);
          updated.set(hazard.id, now);
          
          // Clean up old entries (older than 2 hours)
          const twoHoursAgo = now - (2 * 60 * 60 * 1000);
          for (const [id, time] of updated) {
            if (time < twoHoursAgo) updated.delete(id);
          }
          return updated;
        });
        
        break; // Only one dialog at a time
      }
    }
  }, [hazards, askedHazardTimes, notifiedHazardIds, seenHazardIds, votedHazardIds, hazardConfirmDialog]);
  
  // Start geolocation watch for hazard proximity
  useEffect(() => {
    if (!enabled || hazards.length === 0) {
      // No hazards to track, stop watching
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    
    // Start watching position for hazard proximity
    if (watchIdRef.current === null && 'geolocation' in navigator) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        checkHazardProximity,
        (error) => console.warn('Geolocation error:', error.message),
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
      );
    }
    
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, hazards, checkHazardProximity]);

  // Poll for hazards
  useEffect(() => {
    if (!enabled) return;
    
    // Initial fetch
    fetchHazards();
    
    // Set up polling
    const interval = setInterval(fetchHazards, pollInterval);
    
    return () => clearInterval(interval);
  }, [enabled, pollInterval, fetchHazards]);

  // Show notification for a hazard
  const showHazardNotification = useCallback((hazard: HazardReport) => {
    setNotification({ hazard, timestamp: Date.now() });
    feedbackService.playSound('notification');
    
    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      setNotification(prev => {
        if (prev?.hazard.id === hazard.id) {
          return null;
        }
        return prev;
      });
    }, 8000);
  }, []);

  // Dismiss current notification
  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Set the zoom callback from the map component
  const setZoomCallback = useCallback((callback: ((lat: number, lng: number, zoom?: number) => void) | null) => {
    zoomCallbackRef.current = callback;
  }, []);

  // Zoom to hazard location on the in-app map
  const zoomToHazard = useCallback((hazard: HazardReport) => {
    if (zoomCallbackRef.current) {
      zoomCallbackRef.current(hazard.latitude, hazard.longitude, 17); // Zoom level 17 for street-level detail
      feedbackService.playSound('success');
    }
  }, []);

  // Navigate to hazard location
  const navigateToHazard = useCallback((hazard: HazardReport) => {
    telegram.showPopup({
      title: '🚨 Navigate to Hazard',
      message: `${hazard.type.replace(/_/g, ' ')}`,
      buttons: [
        { id: 'google', type: 'default', text: '🗺️ Google Maps' },
        { id: 'apple', type: 'default', text: '🍎 Apple Maps' },
        { id: 'waze', type: 'default', text: '👻 Waze' }
      ]
    }, (buttonId: string) => {
      if (buttonId === 'google') {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${hazard.latitude},${hazard.longitude}&travelmode=driving`, '_blank');
      } else if (buttonId === 'apple') {
        window.open(`https://maps.apple.com/?daddr=${hazard.latitude},${hazard.longitude}&dirflg=d`, '_blank');
      } else if (buttonId === 'waze') {
        window.open(`https://waze.com/ul?ll=${hazard.latitude},${hazard.longitude}&navigate=yes`, '_blank');
      }
    });
  }, []);

  // Vote on a hazard (yes = still there, no = gone) - uses new voting API
  const voteOnHazard = useCallback(async (hazardId: string, vote: 'yes' | 'no') => {
    if (!userLocationRef.current) {
      feedbackService.playSound('error');
      return;
    }
    
    setVotingInProgress(true);
    try {
      // Convert yes/no to still_there/gone for API
      const voteType = vote === 'yes' ? 'still_there' : 'gone';
      
      const response = await fetch('/api/hazards/vote', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Telegram-User-Id': telegram.getUserId() || '',
        },
        body: JSON.stringify({ 
          hazardId, 
          voteType,
          userId: telegram.getUserId() || 'anonymous',
          userLatitude: userLocationRef.current.lat,
          userLongitude: userLocationRef.current.lon,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        feedbackService.playSound(vote === 'yes' ? 'warning' : 'success');
        
        // Mark this hazard as voted on (persist to localStorage)
        setVotedHazardIds(prev => {
          const updated = new Set(prev);
          updated.add(hazardId);
          // Keep only last 200 voted hazards
          if (updated.size > 200) {
            const arr = Array.from(updated);
            arr.splice(0, arr.length - 200);
            return new Set(arr);
          }
          return updated;
        });
        
        // If hazard was removed, dismiss notification
        if (data.hazardRemoved) {
          dismissNotification();
          // Refresh hazards list
          fetchHazards();
        }
        
        // Close expanded state
        setNotificationExpanded(false);
      } else {
        // Show error feedback (too far, already voted, etc)
        feedbackService.playSound('error');
      }
    } catch (error) {
      console.error('Error voting on hazard:', error);
      feedbackService.playSound('error');
    } finally {
      setVotingInProgress(false);
      setHazardConfirmDialog(null);
    }
  }, [dismissNotification, fetchHazards]);
  
  // Handle hazard confirmation response
  const handleHazardConfirm = useCallback((vote: 'yes' | 'no') => {
    if (hazardConfirmDialog) {
      voteOnHazard(hazardConfirmDialog.id, vote);
    }
  }, [hazardConfirmDialog, voteOnHazard]);

  const meta = notification ? HAZARD_META[notification.hazard.type] : null;
  const isGhost = notification?.hazard.is_ghost;

  return (
    <HazardNotificationContext.Provider
      value={{
        hazards,
        showHazardNotification,
        dismissNotification,
        navigateToHazard,
        voteOnHazard,
        zoomToHazard,
        setZoomCallback,
      }}
    >
      {children}
      
      {/* Hazard Notification Snackbar - Expandable with vote buttons */}
      <Snackbar
        open={!!notification}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 70, sm: 24 } }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: 400,
            background: isGhost 
              ? 'linear-gradient(135deg, rgba(100, 116, 139, 0.95), rgba(71, 85, 105, 0.95))'
              : `linear-gradient(135deg, ${meta?.color}ee, ${meta?.color}cc)`,
            border: `2px solid ${isGhost ? 'rgba(148, 163, 184, 0.5)' : meta?.color}`,
            boxShadow: `0 0 30px ${isGhost ? 'rgba(100, 116, 139, 0.5)' : meta?.color}50`,
            '& .MuiAlert-icon': { display: 'none' },
          }}
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setNotificationExpanded(!notificationExpanded);
                  // Zoom to hazard when expanding
                  if (!notificationExpanded && notification) {
                    zoomToHazard(notification.hazard);
                  }
                }}
                sx={{ color: 'white' }}
              >
                {notificationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => notification && navigateToHazard(notification.hazard)}
                sx={{ color: 'white' }}
              >
                <NavigateIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={dismissNotification}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: 24, color: 'white' },
                opacity: isGhost ? 0.6 : 1,
              }}
            >
              {meta?.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
                {isGhost ? '👻 ' : '⚠️ '}{meta?.label} Ahead!
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {userLocationRef.current && notification && (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem', fontWeight: 600 }}>
                    {(() => {
                      const dist = getDistanceMeters(
                        userLocationRef.current!.lat,
                        userLocationRef.current!.lon,
                        notification.hazard.latitude,
                        notification.hazard.longitude
                      );
                      if (dist < 1000) {
                        return `${Math.round(dist)}m`;
                      } else if (dist < 1609.34) {
                        return `${(dist / 1000).toFixed(1)}km`;
                      } else {
                        return `${(dist / 1609.34).toFixed(1)} mi`;
                      }
                    })()}
                  </Typography>
                )}
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                  {isGhost 
                    ? 'Checkpoint' 
                    : notification?.hazard.note || 'Tap to expand'}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Expandable Details Section */}
          <Collapse in={notificationExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              {/* Distance and Time */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                {userLocationRef.current && notification && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DistanceIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                      {(() => {
                        const dist = getDistanceMeters(
                          userLocationRef.current!.lat,
                          userLocationRef.current!.lon,
                          notification.hazard.latitude,
                          notification.hazard.longitude
                        );
                        return dist < 1000 
                          ? `${Math.round(dist)}m away`
                          : `${(dist / 1609.34).toFixed(1)} mi away`;
                      })()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TimeIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem' }}>
                    {notification && (() => {
                      const mins = Math.floor((Date.now() - new Date(notification.hazard.created_at).getTime()) / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins} min ago`;
                      return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
                    })()}
                  </Typography>
                </Box>
              </Box>
              
              {/* Vote counts if available */}
              {notification && (notification.hazard.still_there_votes || notification.hazard.gone_votes) && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {(notification.hazard.still_there_votes || 0) > 0 && (
                    <Chip 
                      size="small"
                      icon={<YesIcon sx={{ fontSize: 14, color: '#22c55e !important' }} />}
                      label={`${notification.hazard.still_there_votes} confirmed`}
                      sx={{ 
                        background: 'rgba(34, 197, 94, 0.2)', 
                        color: '#86efac',
                        fontSize: '0.75rem',
                      }} 
                    />
                  )}
                  {(notification.hazard.gone_votes || 0) > 0 && (
                    <Chip 
                      size="small"
                      icon={<NoIcon sx={{ fontSize: 14, color: '#ef4444 !important' }} />}
                      label={`${notification.hazard.gone_votes} say gone`}
                      sx={{ 
                        background: 'rgba(239, 68, 68, 0.2)', 
                        color: '#fca5a5',
                        fontSize: '0.75rem',
                      }} 
                    />
                  )}
                </Box>
              )}
              
              {/* Vote Buttons */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<YesIcon />}
                  disabled={votingInProgress}
                  onClick={() => notification && voteOnHazard(notification.hazard.id, 'yes')}
                  sx={{
                    flex: 1,
                    background: 'rgba(34, 197, 94, 0.3)',
                    border: '1px solid rgba(34, 197, 94, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '&:hover': { background: 'rgba(34, 197, 94, 0.5)' },
                  }}
                >
                  Still There
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<NoIcon />}
                  disabled={votingInProgress}
                  onClick={() => notification && voteOnHazard(notification.hazard.id, 'no')}
                  sx={{
                    flex: 1,
                    background: 'rgba(239, 68, 68, 0.3)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '&:hover': { background: 'rgba(239, 68, 68, 0.5)' },
                  }}
                >
                  Gone
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Alert>
      </Snackbar>
      
      {/* Hazard Confirmation Dialog - Works for ALL hazard types */}
      <Dialog
        open={!!hazardConfirmDialog}
        onClose={() => setHazardConfirmDialog(null)}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f1729 100%)',
            border: `2px solid ${hazardConfirmDialog ? (HAZARD_META[hazardConfirmDialog.type]?.color || '#3b82f6') : '#3b82f6'}`,
            borderRadius: 3,
            p: 2,
            minWidth: 320,
            maxWidth: 400,
          }
        }}
      >
        {hazardConfirmDialog && (() => {
          const meta = HAZARD_META[hazardConfirmDialog.type] || HAZARD_META.cop;
          const question = HAZARD_QUESTIONS[hazardConfirmDialog.type] || HAZARD_QUESTIONS.cop;
          const confirmCount = hazardConfirmDialog.confirmations || hazardConfirmDialog.still_there_votes || 0;
          
          return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              {/* Hazard Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${meta.color}, ${meta.color}99)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: `0 0 30px ${meta.color}50`,
                }}
              >
                <Box sx={{ '& svg': { fontSize: 48, color: 'white' } }}>
                  {meta.icon}
                </Box>
              </Box>
              
              {/* Question - Human friendly */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 700, 
                  mb: 1,
                  textShadow: `0 0 10px ${meta.color}50`,
                }}
              >
                {question.emoji} {question.question}
              </Typography>
              
              {/* Context note if available */}
              {hazardConfirmDialog.context_note && (
                <Typography 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)', 
                    mb: 1,
                    fontSize: '0.95rem',
                    fontStyle: 'italic',
                  }}
                >
                  "{hazardConfirmDialog.context_note}"
                </Typography>
              )}
              
              <Typography 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  mb: 2,
                  fontSize: '0.9rem',
                }}
              >
                A fellow worker reported this nearby. Help keep everyone safe!
              </Typography>
              
              {/* Confirmation count badge */}
              {confirmCount > 0 && (
                <Chip
                  icon={<ConfirmIcon sx={{ color: '#22c55e !important' }} />}
                  label={`${confirmCount} confirmation${confirmCount !== 1 ? 's' : ''}`}
                  sx={{
                    mb: 2,
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.5)',
                    color: '#22c55e',
                  }}
                />
              )}
              
              {/* Big YES/NO Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={votingInProgress}
                  onClick={() => handleHazardConfirm('yes')}
                  startIcon={<YesIcon />}
                  sx={{
                    flex: 1,
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${meta.color}ee, ${meta.color})`,
                    },
                    boxShadow: `0 0 20px ${meta.color}40`,
                  }}
                >
                  {question.yesLabel}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  disabled={votingInProgress}
                  onClick={() => handleHazardConfirm('no')}
                  startIcon={<NoIcon />}
                  sx={{
                    flex: 1,
                    py: 2,
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4ade80, #22c55e)',
                    },
                    boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)',
                  }}
                >
                  {question.noLabel}
                </Button>
              </Box>
              
              {/* Skip option */}
              <Button
                onClick={() => setHazardConfirmDialog(null)}
                sx={{
                  mt: 2,
                  color: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': { color: 'rgba(255, 255, 255, 0.8)' },
                }}
              >
                Skip
              </Button>
            </Box>
          );
        })()}
      </Dialog>
    </HazardNotificationContext.Provider>
  );
};

// Hook to use hazard notifications
export const useHazardNotifications = () => {
  const context = useContext(HazardNotificationContext);
  if (!context) {
    throw new Error('useHazardNotifications must be used within HazardNotificationProvider');
  }
  return context;
};

export default HazardNotificationProvider;
